import { useCallback, useEffect, useMemo, useRef } from 'react';

import { t as translate } from 'i18next';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import * as yup from 'yup';

import { AvatarUploadField } from '@/components/atoms/AvatarUploadField/AvatarUploadField';
import { CopyInput } from '@/components/atoms/CopyInput/CopyInput';
import { FieldsetCard } from '@/components/atoms/FieldsetCard/FieldsetCard';
import { SectionLayout } from '@/components/atoms/SectionLayout/SectionLayout';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAccount } from '@/hooks/useAccount';
import { useRegisterSectionSave } from '@/hooks/useRegisterSectionSave';
import { useResetFormOnAccountSwitch } from '@/hooks/useResetFormOnAccountSwitch';
import { useTrackFormState } from '@/hooks/useTrackFormState';
import { Account } from '@/shared/types/account';
import { createYupResolver } from '@/util/forms/createYupResolver';
import { CLEAR_DIRTY_VALUES, KEEP_DIRTY_VALUES } from '@/util/forms/resetOptions';
import { LENGTH_XS } from '@/util/validation/constants';
import { isValidAccountSlug } from '@/util/validation/patterns';

const FORM_ID = 'account-settings-form';

type FormValues = { name: string; slug: string };

interface Props {
  readOnly?: boolean;
}

export const AccountSettingsSection = ({ readOnly = false }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { account, accountId, updateAccount, updateAccountAsync, checkSlugAsync } = useAccount();

  const schema = useMemo(
    () =>
      yup.object({
        name: yup
          .string()
          .trim()
          .required(() => translate('validation.required'))
          .max(LENGTH_XS, () => translate('validation.less_than', { 0: LENGTH_XS })),
        slug: yup.string().test('slug', async function (value) {
          const slug = (value ?? '').trim().toLowerCase();
          if (slug.length === 0) {
            return this.createError({ message: translate('account.slug_errors.empty') });
          }
          if (slug.length > LENGTH_XS) {
            return this.createError({ message: translate('account.slug_errors.max_chars') });
          }
          if (slug === account?.slug) {
            return true;
          }
          if (!isValidAccountSlug(slug)) {
            return this.createError({ message: translate('account.slug_errors.name') });
          }
          const isAvailable = await checkSlugAsync(slug);
          if (!isAvailable) {
            return this.createError({
              message: translate('account.slug_errors.not_available', { 0: slug }),
            });
          }
          return true;
        }),
      }),
    [account?.slug, checkSlugAsync]
  );

  const form = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: createYupResolver<FormValues>(schema),
    resetOptions: KEEP_DIRTY_VALUES,
    values: { name: account?.name ?? '', slug: account?.slug ?? '' },
  });
  const {
    control,
    handleSubmit,
    formState: { isDirty, isSubmitting, dirtyFields },
  } = form;

  useTrackFormState({ isDirty, formId: FORM_ID });
  useResetFormOnAccountSwitch(form);

  const redirectTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(redirectTimer.current), []);

  const onSubmit = async (data: FormValues) => {
    if (readOnly) {
      return;
    }
    const updates: Partial<Account> = {};
    if (dirtyFields.name) {
      updates.name = data.name.trim();
    }
    if (dirtyFields.slug) {
      updates.slug = data.slug.trim().toLowerCase();
    }
    if (!updates.name && !updates.slug) {
      return;
    }
    const updated = await updateAccountAsync(updates);
    form.reset({ name: updated.name, slug: updated.slug }, CLEAR_DIRTY_VALUES);
    if (updates.slug) {
      toast.success(t('account.slug_updated_refreshing'));
      clearTimeout(redirectTimer.current);
      redirectTimer.current = setTimeout(
        () => navigate(`/${updated.slug}/account/information`),
        3000
      );
    } else {
      toast.success(t('account.account_updated'));
    }
  };

  useRegisterSectionSave(FORM_ID, isDirty, handleSubmit, onSubmit);

  const onAvatarUpdate = useCallback(
    (variables: { avatar?: string }) => {
      updateAccount(variables, { onSuccess: () => toast.success(t('account.account_updated')) });
    },
    [t, updateAccount]
  );

  return (
    <SectionLayout title={t('common.general')}>
      <Form {...form}>
        <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
          <FieldsetCard
            isDirty={isDirty}
            isLoading={isSubmitting}
            disabled={!isDirty}
            saveButtonLabel={t('common.save')}
            showSaveButton={!readOnly}
            formId={FORM_ID}
          >
            <div className="space-y-4">
              <AvatarUploadField
                id="account-avatar-upload"
                name={account?.name}
                avatar={account?.avatar}
                onUpdate={onAvatarUpdate}
                readOnly={readOnly}
                label={t('account.change_picture')}
                recommendedHint={t('profile.avatar_recommended')}
                supportedHint={t('profile.avatar_supported')}
              />

              <FormField
                control={control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('account.name.title')}</FormLabel>
                    <FormControl>
                      <Input {...field} readOnly={readOnly} />
                    </FormControl>
                    <p className="text-sm leading-5 text-foreground-muted">
                      {t('account.name.subtitle')}
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="slug"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('account.slug')}</FormLabel>
                    <FormControl>
                      <Input {...field} readOnly={readOnly} />
                    </FormControl>
                    <p className="text-sm leading-5 text-foreground-muted">
                      {t('account.slug_url_label')} {window.location.host}/
                      <span className="text-foreground">{field.value}</span>
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-1.5">
                <Label>{t('account.id')}</Label>
                <CopyInput value={accountId ?? ''} hasTooltip={false} />
              </div>
            </div>
          </FieldsetCard>
        </form>
      </Form>
    </SectionLayout>
  );
};
